import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { MapPin, Phone, Globe, BadgeCheck, Heart, MessageSquare, CheckCircle2, Users, Star, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import api, { useTitle, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ContactModal from "../components/ContactModal";
import AdSlot from "../components/AdSlot";

export default function CompanyPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const date = params.get("date") || "";
  const [data, setData] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactMode, setContactMode] = useState("contact");
  const [fav, setFav] = useState(false);
  const { user } = useAuth();
  useTitle(data?.company?.name || "Firma");

  useEffect(() => {
    const p = date ? `?date=${date}` : "";
    api.get(`/companies/${slug}${p}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [slug, date]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-12"><div className="card h-96 animate-pulse" /></div>;
  const { company, resources } = data;
  const dateQ = date ? `?date=${date}` : "";

  const track = (type) => api.post(`/companies/${company.id}/track`, { type }).catch(() => {});

  const toggleFav = async () => {
    if (!user) return toast.error("Favorilere eklemek için giriş yapın");
    try {
      const r = await api.post("/favorites/toggle", { company_id: company.id });
      setFav(r.data.favorited);
      toast.success(r.data.favorited ? "Favorilere eklendi" : "Favorilerden çıkarıldı");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="relative h-56 sm:h-72 overflow-hidden">
        <img src={company.logo} alt={company.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070C1E] via-[#070C1E]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-4 sm:px-6 pb-6">
          <h1 className="text-2xl sm:text-4xl font-extrabold text-white flex items-center gap-3" data-testid="company-name">
            {company.name} {company.verified && <BadgeCheck size={26} className="text-[#D4AF37]" />}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><MapPin size={15} /> {company.city} / {company.district}</span>
            <span className="badge-gold">{resources.length} kaynak</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <AdSlot placement="company_top" city={company.city} district={company.district} />

          <section className="card p-6">
            <h2 className="font-bold text-lg text-white mb-3">Hakkında</h2>
            <p className="text-sm text-slate-300 leading-relaxed">{company.description || "Bu firma henüz açıklama eklememiş."}</p>
          </section>

          <section>
            <h2 className="font-bold text-lg text-white mb-1">Kaynaklar</h2>
            <p className="text-sm text-slate-400 mb-4">Bu firmanın {resources.length} farklı kaynağı bulunmaktadır.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {resources.map((r) => (
                <Link key={r.id} to={`/firma/${company.slug}/kaynak/${r.id}${dateQ}`} data-testid={`company-resource-${r.id}`}
                  className="card overflow-hidden hover:border-[#D4AF37]/40 hover:scale-[1.01] transition-all group">
                  <div className="h-32 relative">
                    <img src={r.photos?.[0] || company.logo} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
                    {date && (
                      <span className={`absolute top-2 right-2 ${r.busy ? "badge-red" : "badge-green"} backdrop-blur`} data-testid={`resource-status-${r.id}`}>
                        {r.busy ? "Dolu" : "Müsait"}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-bold text-slate-100 group-hover:text-[#D4AF37] transition-colors">{r.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{r.category_name}</div>
                    {r.max_capacity > 0 && (
                      <div className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Users size={12} /> {r.min_capacity}-{r.max_capacity} kişi</div>
                    )}
                    <div className="flex gap-1.5 mt-2">
                      {r.meal_yemekli && <span className="badge-gold !py-0.5 !px-2">Yemekli</span>}
                      {r.meal_yemeksiz && <span className="badge-gold !py-0.5 !px-2">Yemeksiz</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {company.reviews?.length > 0 && (
            <section className="card p-6">
              <h2 className="font-bold text-lg text-white mb-4">Yorumlar</h2>
              <div className="space-y-4">
                {company.reviews.map((rv, i) => (
                  <div key={i} className="border-b border-white/5 last:border-0 pb-4 last:pb-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-slate-200">{rv.name}</span>
                      <span className="flex gap-0.5">{Array.from({ length: rv.rating }).map((_, j) => <Star key={j} size={12} className="fill-[#D4AF37] text-[#D4AF37]" />)}</span>
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{rv.comment}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="space-y-4">
          <div className="card p-6 space-y-3 sticky top-24">
            <button data-testid="contact-request-btn" onClick={() => { setContactMode("contact"); setContactOpen(true); }} className="btn-gold w-full">
              <MessageSquare size={16} /> İletişime Geç
            </button>
            <button data-testid="reservation-request-btn" onClick={() => { setContactMode("reservation"); setContactOpen(true); }} className="btn-ghost w-full !border-[#D4AF37]/50 !text-[#D4AF37] hover:!bg-[#D4AF37]/10">
              <CalendarCheck size={16} /> Rezervasyon İsteği Gönder
            </button>
            {company.whatsapp && (
              <a data-testid="whatsapp-btn" href={`https://wa.me/${company.whatsapp}`} target="_blank" rel="noreferrer"
                onClick={() => track("whatsapp_click")}
                className="btn-ghost w-full !border-[#10B981]/40 !text-[#34D399] hover:!border-[#10B981]">
                WhatsApp'tan Yaz
              </a>
            )}
            {company.phone && (
              <a data-testid="phone-btn" href={`tel:${company.phone}`} onClick={() => track("phone_click")} className="btn-ghost w-full">
                <Phone size={16} /> {company.phone}
              </a>
            )}
            {company.website && (
              <a href={company.website} target="_blank" rel="noreferrer" className="btn-ghost w-full">
                <Globe size={16} /> Web Sitesi
              </a>
            )}
            <button data-testid="favorite-company-btn" onClick={toggleFav}
              className={`btn-ghost w-full ${fav ? "!border-[#EF4444]/50 !text-red-400" : ""}`}>
              <Heart size={16} className={fav ? "fill-red-400" : ""} /> {fav ? "Favorilerde" : "Favorilere Ekle"}
            </button>
            <div className="pt-3 border-t border-white/10 text-xs text-slate-500">
              <div className="flex items-center gap-1.5"><MapPin size={12} /> {company.address}</div>
            </div>
          </div>
          <AdSlot placement="company_bottom" city={company.city} district={company.district} />
        </div>
      </div>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} company={company} defaultDate={date} mode={contactMode} />
    </div>
  );
}
